import { Text } from "@hackthenorth/north";
import { FlagIcon } from "@heroicons/react/24/outline";
import { Button } from "north.js";
import React, { useState } from "react";
import styled from "styled-components";

import { errorToast, successToast } from "src/shared/components";
import { useUserContext } from "src/shared/contexts";
import { PointsIcon } from "src/static/icons";
import { EmptyImage } from "src/static/img";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import { useDeleteGooseGamePrizeMutation } from "src/views/goose-games/graphql/deleteGooseGamePrize.generated";
import { useMyGooseGamePlayerQuery } from "src/views/goose-games/graphql/hacker/myGooseGamePlayer.generated";
import { useUpdateGooseGamePrizeMutation } from "src/views/goose-games/graphql/updateGooseGamePrize.generated";
import { StoreItem } from "src/views/goose-games/types";

import { BalanceChip } from "../../balanceChip";
import { MEDAL_PROGRESS_MAX } from "../../modals/hacker/home/quest/constants";
import PrizeModal from "../../modals/hacker/store/prize";
import {
  getPrizeStatus,
  PrizeStatus,
  STATUS_LABELS,
} from "../../modals/hacker/store/prize/view";
import EditModal from "../../modals/organizer/prize/edit";

const ItemCard = ({
  item,
  toggleCompleted,
  refetchItems = () => {},
}: {
  item: StoreItem;
  toggleCompleted?: (itemId: number, isAvailable: boolean) => void;
  refetchItems?: () => void;
}) => {
  const [updateGooseGamePrize] = useUpdateGooseGamePrizeMutation();
  const [deleteGooseGamePrize] = useDeleteGooseGamePrizeMutation();
  const { isOrganizer } = useUserContext();
  const [modalOpen, setModalOpen] = useState(false);
  const { data: myGGPlayerData } = useMyGooseGamePlayerQuery();

  const currentPoints = isOrganizer
    ? MEDAL_PROGRESS_MAX
    : myGGPlayerData?.myGooseGamePlayer.current_points ?? 0;
  const status = getPrizeStatus(item, currentPoints);
  const isAvailable = status !== "sold-out";
  const pointsAway = item.point_cost - currentPoints;

  const handleEditQuest = async (
    id: number,
    successMessage: string,
    errorMessage: string,
    name?: string,
    pointCost?: number,
    quantityAvailable?: number,
    userRedeptionLimit?: number,
    description?: string,
    imageUrl?: string
  ) => {
    try {
      await updateGooseGamePrize({
        variables: {
          id: id,
          name: name,
          description: description,
          imageUrl: imageUrl,
          pointCost: pointCost,
          quantityAvailable: quantityAvailable,
          userRedemptionLimit: userRedeptionLimit,
        },
      });
      successToast(successMessage);
    } catch (error) {
      errorToast(errorMessage);
      console.error(error);
    } finally {
      refetchItems();
    }
    setModalOpen(false);
  };

  const handleDeleteQuest = async (
    id: number,
    successMessage: string,
    errorMessage: string
  ) => {
    try {
      await deleteGooseGamePrize({
        variables: {
          id: id,
        },
      });
      successToast(successMessage);
    } catch (error) {
      errorToast(errorMessage);
      console.error(error);
    } finally {
      refetchItems();
    }
    setModalOpen(false);
  };

  return (
    <>
      {modalOpen &&
        (isOrganizer ? (
          <EditModal
            item={item}
            isOpen={modalOpen}
            onDelete={handleDeleteQuest}
            onConfirm={handleEditQuest}
            onCancel={() => setModalOpen(false)}
          />
        ) : (
          <PrizeModal
            item={item}
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
          />
        ))}

      <Card onClick={modalOpen ? () => {} : () => setModalOpen(true)}>
        <ItemImage
          src={item.image_url ? item.image_url : EmptyImage}
          alt={`${item.name} Image`}
          isAvailable={isAvailable}
        />
        <ContentWrapper>
          <FlexHeading>
            <TypeContainer>
              <StatusOutline status={status}>
                <StatusText mods="p" status={status}>
                  {STATUS_LABELS[status]}
                </StatusText>
              </StatusOutline>
            </TypeContainer>
            <TypeContainer>
              <BalanceChip isComplete={!isAvailable} points={item.point_cost} />
            </TypeContainer>
          </FlexHeading>
          <TitleText mods="heading h3" isAvailable={isAvailable}>
            {item.name}
          </TitleText>
          <ContentContainer>
            <SubtitleText mods="p" isAvailable={isAvailable}>
              {item.description}
            </SubtitleText>
            {isOrganizer && toggleCompleted && (
              <>
                <ButtonSpacer />
                <MarkButton
                  isComplete={!isAvailable}
                  leadingIcon={<FlagIcon width={20} />}
                  size="md"
                  color="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCompleted(item.id, isAvailable);
                  }}
                >
                  {isAvailable ? 'Mark "Sold out"' : 'Mark "Available"'}
                </MarkButton>
              </>
            )}
            {!isOrganizer &&
              status === "insufficient-funds" &&
              currentPoints >= pointsAway * 0.3 && (
                <TypeContainer>
                  <PointsIcon variant="primary" />
                  <AffordingText mods="p" isAvailable={isAvailable}>
                    {`${pointsAway} points away from affording one!`}
                  </AffordingText>
                </TypeContainer>
              )}
          </ContentContainer>
        </ContentWrapper>
      </Card>
    </>
  );
};

const FlexHeading = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px;

  ${mediaQueries.custom(1128)} {
    justify-content: start;
    flex-direction: column;
  }

  ${mediaQueries.tablet} {
    justify-content: space-between;
    flex-direction: row;
  }
`;

const TypeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const ContentContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MarkButton = styled(Button)<{ isComplete: boolean }>`
  color: ${({ theme, isComplete }) =>
    isComplete
      ? theme.globalConstants.color.black
      : theme.globalConstants.color.textDanger} !important;
  font-weight: 700;
  position: absolute;
  bottom: 0px;
  left: 0px;
`;

const ButtonSpacer = styled.div`
  width: 100%;
  height: 36px;
`;

const StatusOutline = styled.div<{ status: PrizeStatus }>`
  background: ${({ theme, status }) => {
    switch (status) {
      case "redeemable":
        return theme.globalConstants.color.backgroundSuccessLight;
      case "insufficient-funds":
        return theme.globalConstants.color.backgroundWarningLight;
      case "sold-out":
        return theme.globalConstants.color.backgroundDangerLight;
    }
  }};
  display: flex;
  padding: 4px 9.189px;
  align-items: center;
  gap: 4px;
  border-radius: 32px;
  height: 32px;
`;

const StatusText = styled(Text)<{ status: PrizeStatus }>`
  color: ${({ status }) => {
    switch (status) {
      case "redeemable":
        return "#54C152 !important";
      case "insufficient-funds":
        return "#D97706 !important";
      case "sold-out":
        return "#D95858 !important";
      default:
        return "#1F2937 !important";
    }
  }};
  font-size: 14px !important;
  font-weight: 700;
  white-space: nowrap;
`;

const TitleText = styled(Text)<{ isAvailable: boolean }>`
  color: #374151 !important;
  line-height: normal !important;

  opacity: ${({ isAvailable }) => (isAvailable ? 1 : 0.5)};
`;

const SubtitleText = styled(Text)<{ isAvailable: boolean }>`
  color: #6b7280 !important;
  line-height: normal !important;
  overflow: hidden;
  display: -webkit-box !important; /* stylelint-disable-line value-no-vendor-prefix */
  -webkit-box-orient: vertical; /* stylelint-disable-line property-no-vendor-prefix */
  line-clamp: 2;
  -webkit-line-clamp: 2; /* stylelint-disable-line property-no-vendor-prefix */
  text-overflow: ellipsis;
  opacity: ${({ isAvailable }) => (isAvailable ? 1 : 0.5)};

  ${mediaQueries.medium} {
    line-clamp: 4;
    -webkit-line-clamp: 4; /* stylelint-disable-line property-no-vendor-prefix */
  }
`;

const AffordingText = styled(Text)<{ isAvailable: boolean }>`
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary} !important;
  line-height: normal !important;
  overflow: hidden;
  display: -webkit-box !important; /* stylelint-disable-line value-no-vendor-prefix */
  -webkit-box-orient: vertical; /* stylelint-disable-line property-no-vendor-prefix */
  line-clamp: 2;
  -webkit-line-clamp: 2; /* stylelint-disable-line property-no-vendor-prefix */
  text-overflow: ellipsis;
  opacity: ${({ isAvailable }) => (isAvailable ? 1 : 0.5)};
  font-weight: 500;

  ${mediaQueries.medium} {
    line-clamp: 4;
    -webkit-line-clamp: 4; /* stylelint-disable-line property-no-vendor-prefix */
  }
`;

const Card = styled.div`
  display: flex;
  width: 100%;
  padding: 20px;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  border-radius: 8px;
  border: 1px solid var(--North-Border-Gray, #d1d5db);
  background: ${({ theme }) => theme.globalConstants.color.white};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9fafb;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  height: 100%;
`;

const ItemImage = styled.img<{ isAvailable: boolean }>`
  aspect-ratio: 1;
  border-radius: 8px;
  width: 170px;
  height: 170px;
  object-fit: cover;

  opacity: ${({ isAvailable }) => (isAvailable ? 1 : 0.5)};

  ${mediaQueries.large} {
    width: 120px;
    height: 120px;
  }

  ${mediaQueries.medium} {
    width: 170px;
    height: 170px;
  }

  ${mediaQueries.largeMobile} {
    width: 120px;
    height: 120px;
  }
`;

export default ItemCard;
